import { Container } from "@/components/layout/Container";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calendar, ArrowLeft, Clock } from "lucide-react";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { motion } from "framer-motion";
import { Link, useLocation } from "wouter";
import type { Blog } from "@shared/types";

interface BlogDetailProps {
  blog: Blog;
}

export function BlogDetail({ blog }: BlogDetailProps) {
  const [, navigate] = useLocation();

  return (
    <Container className="py-8 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto"
      >
        {/* Back button */}
        <Link href="/blog">
          <Button variant="ghost" className="mb-6 p-0 h-auto">
            <ArrowLeft className="w-4 h-4 mr-2" />
            ブログ一覧に戻る
          </Button>
        </Link>

        {/* Article header */}
        <header className="mb-8">
          {blog.eyecatch && (
            <div className="relative mb-6 rounded-lg overflow-hidden">
              <img
                src={blog.eyecatch.url}
                alt={blog.title}
                className="w-full h-64 md:h-96 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          )}

          <div className="space-y-4">
            <Badge variant="secondary">
              {blog.category?.name || 'カテゴリなし'}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              {blog.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {blog.publishedAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={blog.publishedAt}>
                    公開: {format(new Date(blog.publishedAt), "yyyy年MM月dd日", { locale: ja })}
                  </time>
                </div>
              )}
              
              {blog.revisedAt && blog.publishedAt !== blog.revisedAt && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <time dateTime={blog.revisedAt}>
                    更新: {format(new Date(blog.revisedAt), "yyyy年MM月dd日", { locale: ja })}
                  </time>
                </div>
              )}
            </div>
          </div>
        </header>

        <Separator className="mb-8" />

        {/* Article content */}
        <article 
          className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:leading-relaxed prose-pre:bg-muted prose-pre:border prose-code:bg-muted prose-code:px-1 prose-code:rounded-sm prose-code:before:content-none prose-code:after:content-none"
          dangerouslySetInnerHTML={{ __html: blog.content || '<p>記事の内容が読み込めませんでした。</p>' }}
        />
      </motion.div>
    </Container>
  );
}